import React, { Component } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Download, AlertCircle, TrendingUp, Clock } from 'lucide-react';
// --- Mock Data ---
const receivablesAgingData = [{
  month: 'Jan',
  current: 1200,
  aging30: 320,
  aging60: 180
}, {
  month: 'Feb',
  current: 1350,
  aging30: 280,
  aging60: 150
}, {
  month: 'Mar',
  current: 1280,
  aging30: 350,
  aging60: 210
}, {
  month: 'Apr',
  current: 1420,
  aging30: 290,
  aging60: 170
}, {
  month: 'May',
  current: 1380,
  aging30: 380,
  aging60: 240
}, {
  month: 'Jun',
  current: 1450,
  aging30: 310,
  aging60: 190
}, {
  month: 'Jul',
  current: 1520,
  aging30: 340,
  aging60: 220
}, {
  month: 'Aug',
  current: 1480,
  aging30: 360,
  aging60: 250
}, {
  month: 'Sep',
  current: 1550,
  aging30: 320,
  aging60: 210
}, {
  month: 'Oct',
  current: 1600,
  aging30: 380,
  aging60: 280
}, {
  month: 'Nov',
  current: 1580,
  aging30: 350,
  aging60: 240
}, {
  month: 'Dec',
  current: 1650,
  aging30: 390,
  aging60: 310
}];
const topBrokersData = [{
  rank: 1,
  broker: 'Marsh Ltd',
  outstanding: 450000,
  current: 320000,
  aging30: 85000,
  aging60: 45000,
  contactDate: '2024-12-15',
  status: 'Follow-up'
}, {
  rank: 2,
  broker: 'Aon UK',
  outstanding: 380000,
  current: 280000,
  aging30: 70000,
  aging60: 30000,
  contactDate: '2024-12-10',
  status: 'Current'
}, {
  rank: 3,
  broker: 'Willis Towers Watson',
  outstanding: 320000,
  current: 250000,
  aging30: 50000,
  aging60: 20000,
  contactDate: '2024-12-12',
  status: 'Current'
}, {
  rank: 4,
  broker: 'Howden Group',
  outstanding: 280000,
  current: 180000,
  aging30: 65000,
  aging60: 35000,
  contactDate: '2024-12-18',
  status: 'Follow-up'
}, {
  rank: 5,
  broker: 'Arthur J. Gallagher',
  outstanding: 250000,
  current: 200000,
  aging30: 35000,
  aging60: 15000,
  contactDate: '2024-12-08',
  status: 'Current'
}, {
  rank: 6,
  broker: 'PIB Group',
  outstanding: 220000,
  current: 140000,
  aging30: 50000,
  aging60: 30000,
  contactDate: '2024-12-20',
  status: 'Escalated'
}, {
  rank: 7,
  broker: 'Ardonagh Group',
  outstanding: 180000,
  current: 150000,
  aging30: 20000,
  aging60: 10000,
  contactDate: '2024-12-05',
  status: 'Current'
}, {
  rank: 8,
  broker: 'Besso Insurance',
  outstanding: 150000,
  current: 100000,
  aging30: 35000,
  aging60: 15000,
  contactDate: '2024-12-14',
  status: 'Follow-up'
}];
const collectionRatioData = [{
  month: 'Jan',
  ratio: 92
}, {
  month: 'Feb',
  ratio: 94
}, {
  month: 'Mar',
  ratio: 91
}, {
  month: 'Apr',
  ratio: 93
}, {
  month: 'May',
  ratio: 90
}, {
  month: 'Jun',
  ratio: 95
}, {
  month: 'Jul',
  ratio: 93
}, {
  month: 'Aug',
  ratio: 92
}, {
  month: 'Sep',
  ratio: 94
}, {
  month: 'Oct',
  ratio: 91
}, {
  month: 'Nov',
  ratio: 93
}, {
  month: 'Dec',
  ratio: 92
}];
// --- Colors ---
const COLORS = {
  current: '#10B981',
  aging30: '#F59E0B',
  aging60: '#DC2626',
  ratio: '#3B82F6',
  target: '#9CA3AF' // Gray
};
// --- Helper Functions ---
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Current':
      return 'bg-green-100 text-green-800';
    case 'Follow-up':
      return 'bg-amber-100 text-amber-800';
    case 'Escalated':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
const getAgingPercentage = (current: number, aging30: number, aging60: number) => {
  const total = current + aging30 + aging60;
  return {
    current: current / total * 100,
    aging30: aging30 / total * 100,
    aging60: aging60 / total * 100
  };
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '£K'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1373">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1374">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1375">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1376" />
            <span className="text-gray-600" data-id="element-1377">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-1378">
              {unit === '£K' ? '£' : ''}
              {entry.value.toLocaleString()}
              {unit === '£K' ? 'K' : unit === '%' ? '%' : ''}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function CreditControlOperations() {
  const latestData = receivablesAgingData[receivablesAgingData.length - 1];
  const totalReceivables = latestData.current + latestData.aging30 + latestData.aging60;
  const totalOverdue = latestData.aging30 + latestData.aging60;
  const overduePercentage = totalOverdue / totalReceivables * 100;
  const latestCollectionRatio = collectionRatioData[collectionRatioData.length - 1].ratio;
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1379">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1380">
        <div data-id="element-1381">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1382">
            Credit Control & Operations
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1383">
            Liquidity and counterparty risk monitoring • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1384">
          {overduePercentage > 30 && <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full border border-amber-200" data-id="element-1385">
              <AlertCircle className="text-amber-600" size={16} data-id="element-1386" />
              <span className="text-sm font-medium text-amber-700" data-id="element-1387">
                {overduePercentage.toFixed(0)}% Overdue
              </span>
            </div>}
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1388">
            <Download size={16} className="mr-2" data-id="element-1389" />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1390">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1391">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1392">
            Total Receivables
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1393">
            £{(totalReceivables / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1394">Outstanding premium</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4 bg-red-50" data-id="element-1395">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1396">
            Total Overdue
          </p>
          <p className="text-2xl font-bold text-red-600" data-id="element-1397">
            £{(totalOverdue / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-red-700 mt-1" data-id="element-1398">
            {overduePercentage.toFixed(0)}% of total
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1399">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1400">
            Collection Ratio
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1401">
            {latestCollectionRatio}%
          </p>
          <p className="text-xs text-green-600 mt-1" data-id="element-1402">Above 90% target</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1403">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1404">
            Avg Days Outstanding
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1405">38 days</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1406">Within policy</p>
        </div>
      </div>

      {/* Top Row: Receivables Aging */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1407">
        <div className="flex justify-between items-center mb-6" data-id="element-1408">
          <div data-id="element-1409">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1410">
              Receivables Aging
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1411">
              Monthly breakdown by aging bucket
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm" data-id="element-1412">
            <div className="flex items-center gap-2" data-id="element-1413">
              <div className="w-3 h-3 rounded bg-green-600" data-id="element-1414"></div>
              <span className="text-gray-600" data-id="element-1415">0-30 days</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1416">
              <div className="w-3 h-3 rounded bg-amber-500" data-id="element-1417"></div>
              <span className="text-gray-600" data-id="element-1418">30-60 days</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1419">
              <div className="w-3 h-3 rounded bg-red-600" data-id="element-1420"></div>
              <span className="text-gray-600" data-id="element-1421">60+ days</span>
            </div>
          </div>
        </div>
        <div className="h-80" data-id="element-1422">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1423">
            <BarChart data={receivablesAgingData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1424">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1425" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1426" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `£${value}K`} data-id="element-1427" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1429" />} data-id="element-1428" />
              <Bar dataKey="current" name="0-30 days" stackId="a" fill={COLORS.current} radius={[0, 0, 0, 0]} data-id="element-1430" />
              <Bar dataKey="aging30" name="30-60 days" stackId="a" fill={COLORS.aging30} radius={[0, 0, 0, 0]} data-id="element-1431" />
              <Bar dataKey="aging60" name="60+ days" stackId="a" fill={COLORS.aging60} radius={[4, 4, 0, 0]} data-id="element-1432" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm" data-id="element-1433">
          <div className="border-r border-gray-200" data-id="element-1434">
            <p className="text-gray-500" data-id="element-1435">Current (0-30)</p>
            <p className="text-xl font-bold text-green-600 mt-1" data-id="element-1436">
              £{(latestData.current / 1000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-400" data-id="element-1437">
              {(latestData.current / totalReceivables * 100).toFixed(0)}%
            </p>
          </div>
          <div className="border-r border-gray-200" data-id="element-1438">
            <p className="text-gray-500" data-id="element-1439">30-60 days</p>
            <p className="text-xl font-bold text-amber-600 mt-1" data-id="element-1440">
              £{(latestData.aging30 / 1000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-400" data-id="element-1441">
              {(latestData.aging30 / totalReceivables * 100).toFixed(0)}%
            </p>
          </div>
          <div data-id="element-1442">
            <p className="text-gray-500" data-id="element-1443">60+ days</p>
            <p className="text-xl font-bold text-red-600 mt-1" data-id="element-1444">
              £{(latestData.aging60 / 1000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-400" data-id="element-1445">
              {(latestData.aging60 / totalReceivables * 100).toFixed(0)}%
            </p>
          </div>
        </div>
      </div>

      {/* Middle Row: Collection Ratio */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1446">
        <div className="flex justify-between items-center mb-6" data-id="element-1447">
          <div data-id="element-1448">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1449">
              Collection Ratio Over Time
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1450">
              Percentage of receivables collected within 30 days
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1451">
            <div className="w-4 h-0.5 bg-gray-400 border-t border-dashed" data-id="element-1452"></div>
            <span data-id="element-1453">Target (90%)</span>
          </div>
        </div>
        <div className="h-64" data-id="element-1454">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1455">
            <LineChart data={collectionRatioData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1456">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1457" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1458" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `${value}%`} domain={[85, 100]} data-id="element-1459" />
              <Tooltip content={<CustomTooltip unit="%" data-id="element-1461" />} data-id="element-1460" />
              <ReferenceLine y={90} stroke={COLORS.target} strokeDasharray="5 5" strokeWidth={2} label={{
              value: 'Target',
              position: 'right',
              fill: '#9CA3AF',
              fontSize: 12
            }} data-id="element-1462" />
              <Line type="monotone" dataKey="ratio" name="Collection Ratio" stroke={COLORS.ratio} strokeWidth={3} dot={{
              r: 4
            }} activeDot={{
              r: 6
            }} data-id="element-1463" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row: Top Brokers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1464">
        <div className="flex justify-between items-center mb-6" data-id="element-1465">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1466">
            Top Brokers by Outstanding Balance
          </h2>
          <div className="text-sm text-gray-500" data-id="element-1467">
            Total:{' '}
            <span className="font-semibold text-gray-900" data-id="element-1468">
              £
              {(topBrokersData.reduce((sum, b) => sum + b.outstanding, 0) / 1000000).toFixed(1)}
              M
            </span>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1469">
          <table className="w-full" data-id="element-1470">
            <thead data-id="element-1471">
              <tr className="border-b-2 border-gray-200" data-id="element-1472">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1473">
                  Rank
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1474">
                  Broker
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1475">
                  Total Outstanding
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1476">
                  Aging Breakdown
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1477">
                  Last Contact
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1478">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1479">
              {topBrokersData.map(broker => {
              const percentages = getAgingPercentage(broker.current, broker.aging30, broker.aging60);
              return <tr key={broker.rank} className="hover:bg-gray-50 transition-colors" data-id="element-1480">
                    <td className="py-3 px-4 text-sm text-gray-500" data-id="element-1481">
                      {broker.rank}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900" data-id="element-1482">
                      {broker.broker}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-1483">
                      £{(broker.outstanding / 1000).toFixed(0)}K
                    </td>
                    <td className="py-3 px-4" data-id="element-1484">
                      <div className="flex items-center gap-1" data-id="element-1485">
                        <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden flex" data-id="element-1486">
                          <div className="bg-green-500" style={{
                        width: `${percentages.current}%`
                      }} title={`Current: £${(broker.current / 1000).toFixed(0)}K`} data-id="element-1487" />
                          <div className="bg-amber-500" style={{
                        width: `${percentages.aging30}%`
                      }} title={`30-60: £${(broker.aging30 / 1000).toFixed(0)}K`} data-id="element-1488" />
                          <div className="bg-red-500" style={{
                        width: `${percentages.aging60}%`
                      }} title={`60+: £${(broker.aging60 / 1000).toFixed(0)}K`} data-id="element-1489" />
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-center text-gray-600" data-id="element-1490">
                      {new Date(broker.contactDate).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short'
                  })}
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-1491">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(broker.status)}`} data-id="element-1492">
                        {broker.status}
                      </span>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500" data-id="element-1493">
          <div className="flex items-center gap-4" data-id="element-1494">
            <div className="flex items-center gap-2" data-id="element-1495">
              <div className="w-3 h-3 rounded bg-green-500" data-id="element-1496"></div>
              <span data-id="element-1497">0-30 days</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1498">
              <div className="w-3 h-3 rounded bg-amber-500" data-id="element-1499"></div>
              <span data-id="element-1500">30-60 days</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1501">
              <div className="w-3 h-3 rounded bg-red-500" data-id="element-1502"></div>
              <span data-id="element-1503">60+ days</span>
            </div>
          </div>
          <p className="italic" data-id="element-1504">Hover over aging bars for detailed breakdown</p>
        </div>
      </div>
    </div>;
}